import type { JourneyLeg, JourneyPoint, JourneyRoute, JourneyStop, JourneyTimeline } from '../../../core/travel';

export type TimelineItem = { kind: 'leg'; leg: JourneyLeg; index: number } | { kind: 'stop'; stop: JourneyStop; index: number };
/** A stop begins where its preceding leg ends; ties keep arrival before departure. */
export function journeyTimelineItems(journey: Pick<JourneyTimeline, 'stops' | 'legs'>): TimelineItem[] {
  const items: TimelineItem[] = [...journey.legs.map((leg, index) => ({ kind: 'leg' as const, leg, index })), ...journey.stops.map((stop, index) => ({ kind: 'stop' as const, stop, index }))];
  return items.sort((a, b) => {
    const time = (item: TimelineItem) => Date.parse(item.kind === 'leg' ? item.leg.departedAt : item.stop.arrivedAt);
    const difference = time(a) - time(b);
    if (difference) return difference;
    if (a.kind === b.kind) return a.index - b.index;
    const leg = a.kind === 'leg' ? a.leg : (b as Extract<TimelineItem, { kind: 'leg' }>).leg;
    const stop = a.kind === 'stop' ? a.stop : (b as Extract<TimelineItem, { kind: 'stop' }>).stop;
    const legFirst = leg.toStopId === stop.id;
    return a.kind === 'leg' ? (legFirst ? -1 : 1) : (legFirst ? 1 : -1);
  });
}
export function legTotals(journey: JourneyTimeline, leg: JourneyLeg) {
  return journey.totals?.legs?.find(total => total.fromStopId === (leg.fromStopId ?? null) && total.toStopId === (leg.toStopId ?? null));
}
/** Selection only uses observed points inside this leg, never joins disjoint segments. */
export function selectedLegSegments(route: JourneyRoute, leg: JourneyLeg | undefined): JourneyPoint[][] {
  if (!leg) return [];
  const start = Date.parse(leg.departedAt); const end = leg.arrivedAt ? Date.parse(leg.arrivedAt) : Infinity;
  return route.segments.map(segment => segment.filter(point => Date.parse(point.recordedAt) >= start && Date.parse(point.recordedAt) <= end)).filter(segment => segment.length > 0);
}
export function mergeRoutePage(previous: JourneyRoute, next: JourneyRoute): JourneyRoute {
  // Pagination boundaries remain disconnected: there is no evidence of a continuous
  // line between two independently filtered pages.
  const seen = new Set(previous.segments.flat().map(point => point.sequence));
  const gaps = [...previous.gaps];
  for (const gap of next.gaps) if (!gaps.some(old => old.from === gap.from && old.to === gap.to && old.reason === gap.reason)) gaps.push(gap);
  return { ...next, stops: next.stops ?? previous.stops, segments: [...previous.segments, ...next.segments.map(segment => segment.filter(point => !seen.has(point.sequence))).filter(segment => segment.length)], gaps };
}
